// =====================================================================================
// INVRT Radar — web console — consent banner designer — the studio
//
// Owns the form state for one site's banner: seeded from GET /sites/:id/consent (the
// house defaults when the server says the site has never published), checked against the
// server's own rules before anything leaves the browser, published whole with
// PUT /sites/:id/banner, previewed live by the real loader. Nothing is stored until the
// tenant chooses Publish; Reset only touches the form.
//
// Errors appear after the first publish attempt and then follow the form live, so a field
// stops complaining the moment it is fixed.
// =====================================================================================

import { useMemo, useState } from "react";
import { ErrorState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import type { ConsentSettings } from "@/lib/types.c1";
import { BannerDesigner } from "./BannerDesigner";
import {
  type BannerFormErrors,
  configFromForm,
  DEFAULT_BANNER_CONFIG,
  formFromConfig,
  validateBannerForm,
} from "./bannerForm";
import { BannerPreview } from "./BannerPreview";
import { PublishedSummary } from "./PublishedSummary";
import { type PublishBannerResponse, usePublishBanner } from "./useConsent";

const NO_ERRORS: BannerFormErrors = {};

export interface BannerStudioProps {
  siteId: string;
  settings: ConsentSettings;
  isAdmin: boolean;
}

export function BannerStudio({ siteId, settings, isAdmin }: BannerStudioProps) {
  const { push } = useToast();
  const publish = usePublishBanner(siteId);
  const [form, setForm] = useState(() =>
    formFromConfig(settings.banner_is_default ? DEFAULT_BANNER_CONFIG : settings.banner),
  );
  const [attempted, setAttempted] = useState(false);
  const [lastPublish, setLastPublish] = useState<PublishBannerResponse | null>(null);

  const errors = useMemo(() => (attempted ? validateBannerForm(form) : NO_ERRORS), [attempted, form]);
  const previewConfig = useMemo(() => configFromForm(form), [form]);

  function handlePublish(): void {
    setAttempted(true);
    if (Object.keys(validateBannerForm(form)).length > 0) return;
    publish.mutate(configFromForm(form), {
      onSuccess: (published) => {
        setLastPublish(published);
        push({
          tone: "success",
          title: `Banner published (version ${published.banner_version})`,
        });
      },
    });
  }

  function handleReset(): void {
    setForm(formFromConfig(DEFAULT_BANNER_CONFIG));
    setAttempted(false);
  }

  const errorCount = Object.keys(errors).length;

  return (
    <div className="flex flex-col gap-5">
      <PublishedSummary
        siteId={siteId}
        settings={settings}
        hostedBannerDisableFailed={lastPublish?.details?.hosted_banner === "disable_failed"}
        warnings={lastPublish?.warnings ?? []}
      />

      <div className="grid gap-5 xl:grid-cols-2">
        <BannerDesigner value={form} errors={errors} disabled={!isAdmin} onChange={setForm} />
        <div className="xl:sticky xl:top-4 xl:self-start">
          <BannerPreview config={previewConfig} />
        </div>
      </div>

      {isAdmin ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" onClick={handlePublish} loading={publish.isPending}>
              Publish banner
            </Button>
            <Button type="button" variant="secondary" onClick={handleReset} disabled={publish.isPending}>
              Reset to defaults
            </Button>
            {errorCount > 0 ? (
              <p className="text-sm text-danger" role="alert">
                {errorCount === 1
                  ? "One field needs attention before the banner can be published."
                  : `${errorCount} fields need attention before the banner can be published.`}
              </p>
            ) : null}
          </div>
          <p className="text-xs text-ink-muted">
            Publishing replaces the live banner for every visitor, turns off the hosted banner
            and records a dated copy of this configuration.
          </p>
          {publish.isError ? <ErrorState error={publish.error} /> : null}
        </div>
      ) : null}
    </div>
  );
}
